// Billing B4.2: the booking box asks the guest count and "alcohol: yes / no".
// Browser copy of parseGuestCount in functions/helper/booking-rules.js. The
// server checks again, so a difference here can only block, never let a bad
// count in. functions/test/guests-copy.test.js fails if the two copies differ.

import { FEES } from './fees';

// More guests than this book through support@yedding.com (D-0019, D-0021).
export const MAX_ONLINE_GUESTS = 500;

/**
 * A guest count: a whole number, 1 or more, from 120, '120' or ' 1,200 '.
 * Null for anything else ('', 0, -5, '50.5', '120 guests'). No upper limit here.
 */
export function parseGuestCount(value) {
  if (typeof value !== 'string' && typeof value !== 'number') return null;
  const text = String(value).replace(/[\s,]/g, '');
  if (!/^\d{1,7}$/.test(text)) return null;
  const count = Number(text);
  return count >= 1 ? count : null;
}

// Approved copy (Braeden, 2026-10-31). Same text as the server's refusals.
// "We will help you book" only on venues that allow 501+ guests.
export const GUEST_MESSAGES = {
  hint: 'Not sure? Enter the higher number, so you get the right level of insurance.',
  noGuests: 'Enter the number of guests you expect to attend.',
  overCapacity: (max) => `This venue allows up to ${max} guests. To ask about a larger group, email support@yedding.com.`,
  tooMany: `For more than ${MAX_ONLINE_GUESTS} guests, email support@yedding.com. We will help you book.`,
  tooManyNoMax: `For more than ${MAX_ONLINE_GUESTS} guests, email support@yedding.com.`,
  noAlcohol: 'Choose Yes or No.'
};

// Billing B3.3: the line under "Will alcohol be served?" (approved by Braeden,
// 2026-11-02). Amounts from the fee settings (fees.js, D-0076). Split: the fee
// comes with the second payment; paid in full: today.
const dollars = (cents) => `$${(cents / 100).toLocaleString('en-US')}`;
export function alcoholHint(split) {
  const [small, large] = FEES.alcoholFee;
  return (
    `Yes adds an alcohol fee: ${dollars(small.cents)} for up to ${small.maxGuests} guests, ` +
    `${dollars(large.cents)} for ${small.maxGuests + 1} to ${large.maxGuests} guests. ` +
    `${split ? 'We charge it with your second payment.' : 'We charge it today.'} ` +
    'If you cancel your event, we refund the alcohol fee in full.'
  );
}

/** What the guest box keeps as the booker types: digits only, no leading 0, at most 7. */
export function guestDigits(text) {
  return String(text ?? '').replace(/\D/g, '').replace(/^0+/, '').slice(0, 7);
}

/**
 * The booking box's check. Returns { guestCount, guestError, alcoholError };
 * an error is '' when the field is fine.
 */
export function checkGuestFields({ guestText, alcohol, maxGuests }) {
  const guestCount = parseGuestCount(guestText);
  const max = parseGuestCount(maxGuests);
  let guestError = '';
  if (!guestCount) guestError = GUEST_MESSAGES.noGuests;
  else if (max && guestCount > max) guestError = GUEST_MESSAGES.overCapacity(max);
  else if (guestCount > MAX_ONLINE_GUESTS) guestError = max ? GUEST_MESSAGES.tooMany : GUEST_MESSAGES.tooManyNoMax;
  const alcoholError = typeof alcohol === 'boolean' ? '' : GUEST_MESSAGES.noAlcohol;
  return { guestCount, guestError, alcoholError };
}

/** The search bar's guest count, to fill in the box. '' when there is none. */
export function guestTextFromSearch(guests) {
  const count = parseGuestCount(guests);
  return count ? String(count) : '';
}
